<template>
	<div class="card !shadow-none !border-0">
		<QuizStepper orientation="vertical">
			<StepperPanel
				v-for="(question, index) in questions"
				:key="index"
				:active="currentQuestionIndex === index"
				:header="labels.questionLabel + (index + 1)"
				:class="{
					'exam-locked': isCurrentlyExamMode && completedQuestions.has(index),
					'exam-current': isCurrentlyExamMode && index === currentQuestionIndex
				}"
			>
				<template #content="{ prevCallback, nextCallback }">
					<div
						class="flex flex-col"
						:class="{
							'opacity-50 pointer-events-none':
								isCurrentlyExamMode && completedQuestions.has(index)
						}"
					>
						<InplaceEdit
							class="!items-start"
							:inputProp="{
								content: question,
								inputType: 'quiz',
								propName: 'content'
							}"
							method="PUT"
							:href="question._links?.self.href"
							blockTitle="Edit Question"
							:showAssistant="true"
						>
							<span class="font-semibold">{{ question.question }}</span>
						</InplaceEdit>

						<!-- ANSWERS -->
						<div
							class="py-5 flex-auto flex flex-col justify-center items-start font-semibold"
						>
							<div
								v-for="(answer, aIndex) in question.answers"
								:key="aIndex"
								class="p-4 w-full bg-gray-100"
							>
								<div class="text-md flex items-center">
									<!-- Exam mode: Radio -->
									<QuizRadioButton
										v-if="isCurrentlyExamMode"
										:value="aIndex"
										v-model="selectedAnswers[index]"
										:inputId="`answer-${index + 1}-${aIndex}-${answer.answer?.length}`"
										:name="`question-${index}`"
									/>
									<!-- Quiz mode: Checkbox -->
									<QuizCheck
										v-else
										:value="aIndex"
										v-model="selectedAnswers[index]"
										:inputId="`answer-${index + 1}-${aIndex}-${answer.answer?.length}`"
										:name="`answer-${index + 1}-${aIndex}-${answer.answer?.length}`"
									/>
									<label
										:for="`answer-${index + 1}-${aIndex}-${answer.answer?.length}`"
										class="ml-2 font-normal"
									>
										{{ answer.answer }}
									</label>
								</div>

								<!-- Feedback visible only in quiz mode -->
								<div v-if="isCurrentlyTestMode && isAnswerSelected(index, aIndex)">
									<div
										v-if="answer.isCorrect"
										class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-success/40 bg-success-100/10 text-dark"
									>
										<i
											class="tabler:circle-check !text-[35px] text-success max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"
										></i>
										<span class="text-sm">{{ answer.explanation }}</span>
									</div>

									<div
										v-else
										class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-danger/40 bg-danger-100/10 text-dark"
									>
										<i
											class="tabler:circle-x !text-[35px] text-danger max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"
										></i>
										<span class="text-sm">{{ answer.explanation }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- NAVIGATION BUTTONS -->
					<div class="flex pt-4 justify-between">
						<!-- Back button (only in quiz mode) -->
						<EwButton
							v-if="index && isCurrentlyTestMode && !hasFinished"
							label="Back"
							type="secondary"
							icon="tabler:arrow-narrow-left"
							@click="prevCallback"
						/>

						<!-- Next button -->
						<EwButton
							v-if="index < questions.length - 1"
							type="success"
							label="Next"
							iconAppend="tabler:arrow-narrow-right"
							:loading="isSubmittingAnswer"
							@click="() => handleExamNext(nextCallback)"
						/>

						<!-- Finish button -->
						<EwButton
							v-if="questions.length - 1 === index"
							type="success"
							label="Finish"
							iconAppend="tabler:check"
							:disabled="hasFinished"
							:loading="isSubmittingAnswer"
							@click="isCurrentlyExamMode ? handleFinish() : handleClose()" />


						<!-- Close button appears after Finish -->
						<EwButton
							v-if="hasFinished"
							type="secondary"
							label="Close"
							icon="tabler:x"
							@click="handleClose"
						/>
					</div>
				</template>
			</StepperPanel>
		</QuizStepper>
		<div ref="swalContainer" class="absolute top-20 right-20 z-2 flex justify-center pointer-events-none"></div>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { messages } from '@/assets/language';
import type { configCollectionMode, LanguageCodes, QuizQuestionsItem, SubmittedQuizPayload } from '@/types';
import InplaceEdit from './InplaceEdit/InplaceEdit.vue';
import { useAppStore } from '@/stores/app-store';
import Swal from 'sweetalert2';

const { currentLng } = useAppStore();
const labels = computed(() => {
  const key = currentLng.value as keyof typeof messages;
  return messages[key] ?? {};
});

type QuizMode = 'quiz' | 'exam';

const props = defineProps<{
  selectedLanguage?: LanguageCodes;
  jsonData: {
    postQuiz: { questions: QuizQuestionsItem[] };
    _links: {
      postQuiz: { href: string };
      postQuizEdit: { href: string };
      postQuizAssesment: { href: string };
    };
  };
  collectionMode?: configCollectionMode | null;
}>();

const emits = defineEmits(['close', 'submit']);

// ✅ Mode aktual në bazë të collectionMode.id
const currentMode = computed<QuizMode>(() =>
  props.collectionMode?.id === 'audience' ? 'quiz' : 'quiz'
);
const isCurrentlyExamMode = computed(() => currentMode.value === 'exam');
const isCurrentlyTestMode = computed(() => currentMode.value === 'quiz');

const questions = computed(() => props.jsonData.postQuiz.questions);
const currentQuestionIndex = ref(0);
const completedQuestions = ref<Set<number>>(new Set());
const isSubmittingAnswer = ref(false);
const hasFinished = ref(false);

const swalContainer = ref<HTMLElement | null>(null);

// ✅ Inicimi i përgjigjeve në varësi të mode
const selectedAnswers = ref<Array<Array<number> | number>>(
  props.jsonData.postQuiz.questions.map(() =>
    isCurrentlyExamMode.value ? -1 : []
  )
);

// ✅ Reset kur ndryshon mode
watch(currentMode, (newMode) => {
  selectedAnswers.value = props.jsonData.postQuiz.questions.map(() =>
    newMode === 'exam' ? -1 : []
  );
});

// ✅ Check nëse një përgjigje është zgjedhur
const isAnswerSelected = (questionIndex: number, answerIndex: number) => {
  if (isCurrentlyExamMode.value) {
    return selectedAnswers.value[questionIndex] === answerIndex;
  } else {
    return (selectedAnswers.value[questionIndex] as number[]).includes(answerIndex);
  }
};

// ✅ Next në exam mode
const handleExamNext = (nextCallback: () => void) => {
  currentQuestionIndex.value++;
  nextCallback();
};

// ✅ Submit/Finish funksioni
const handleFinish = async () => {
  const payloadQuestions = questions.value.map((question: QuizQuestionsItem, index) => {
    const selected = selectedAnswers.value[index];

    if (isCurrentlyExamMode.value) {
      const selectedAnswer = question.answers?.[selected as number];
      return {
        id: question.id,
        answers: selectedAnswer
          ? [
              {
                answerLabel: selectedAnswer.answerLabel ?? '',
                answer: selectedAnswer.answer ?? '',
                isCorrectProvided: 'true'
              }
            ]
          : []
      };
    }

    const selectedIndexes = selected as number[];
    return {
      id: question.id,
      answers: selectedIndexes.map((i) => {
        const a = question.answers?.[i];
        return {
          answerLabel: a?.answerLabel ?? '',
          answer: a?.answer ?? '',
          isCorrectProvided: a?.isCorrect ? 'true' : 'false'
        };
      })
    };
  });

  const finalPayload: SubmittedQuizPayload = {
    mode: currentMode.value,
    questions: payloadQuestions
  };

  try {
    const response = await fetch(props.jsonData._links.postQuizAssesment.href, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(finalPayload)
    });

    if (!response.ok) throw new Error(`Request failed: ${response.status}`);

    await Swal.fire({
      icon: 'success',
      title: 'Thank you!',
      text: 'Your answers were submitted successfully.',
      toast: true,
      timer: 2500,
      target: swalContainer.value ?? undefined,
      position: 'center',
      showConfirmButton: false
    });

    emits('submit', finalPayload);
  } catch (err) {
    console.error('❌ Submission error:', err);
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to submit your quiz.',
      toast: true,
      timer: 3000,
      target: swalContainer.value ?? undefined,
      position: 'center',
      showConfirmButton: false
    });
  }

  hasFinished.value = true;
};

// ✅ Close
const handleClose = () => {
  emits('close');
};
</script>


<style scoped>
/* .p-stepper {
	flex-basis: 50rem;
} */
</style>
